import pMap from 'p-map';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
} from '../../Components';
import { apiClient } from '../../state';
import type {
    IntegrationSiteInstallationExtended,
    IntegrationSpaceInstallationExtended,
} from './types';

/**
 * Uninstall an integration in an organization
 */
export async function uninstallIntegrationFromOrganization(target: {
    integration: api.Integration;
    installation: api.IntegrationInstallation;
}) {
    const { integration, installation } = target;

    await apiClient.integrations.uninstallIntegration(integration.name, installation.id);

    analytics.logEvent({
        type: 'integration_uninstall',
        integration: integration.name,
        organization: installation.target.organization,
        installedSpaces: installation.spaces,
    });
}

/**
 * Uninstall the integration from a selected space
 */
export async function uninstallIntegrationFromSpace(target: {
    integrationName: string;
    installationId: string;
    spaceId: string;
    organizationId: string;
}) {
    const { integrationName, installationId, spaceId, organizationId } = target;

    await apiClient.integrations.uninstallIntegrationFromSpace(
        integrationName,
        installationId,
        spaceId
    );

    analytics.logEvent({
        type: 'integration_space_uninstall',
        integration: integrationName,
        space: spaceId,
        organization: organizationId,
    });
}

/**
 * Uninstall the integration from a selected site
 */
export async function uninstallIntegrationFromSite(target: {
    integrationName: string;
    installationId: string;
    siteId: string;
    organizationId: string;
}) {
    const { integrationName, installationId, siteId, organizationId } = target;

    await apiClient.integrations.uninstallIntegrationFromSite(
        integrationName,
        installationId,
        siteId
    );

    analytics.logEvent({
        type: 'integration_site_uninstall',
        integration: integrationName,
        site: siteId,
        organization: organizationId,
    });
}

/**
 * Update the integration installation site selection
 *
 * -- ALL: all sites
 * -- SELECTED: selected sites
 */
export async function updateIntegrationInstallationSiteSelection(target: {
    integrationName: string;
    installation: api.IntegrationInstallation;
    organizationId: string;
    selection: api.IntegrationInstallationSiteSelection;
}) {
    const { integrationName, installation, organizationId, selection } = target;

    if (installation.site_selection === selection) {
        return;
    }

    await apiClient.integrations.updateIntegrationInstallation(integrationName, installation.id, {
        site_selection: selection,
    });

    analytics.logEvent({
        type: 'integration_installation_toggle_site_selection',
        integration: integrationName,
        organization: organizationId,
        siteSelection: selection,
    });
}

/**
 * Update the integration installation space selection
 *
 * -- ALL: all spaces
 * -- SELECTED: selected spaces
 */
export async function updateIntegrationInstallationSpaceSelection(target: {
    integrationName: string;
    installation: api.IntegrationInstallation;
    organizationId: string;
    selection: api.IntegrationInstallationSpaceSelection;
}) {
    const { integrationName, installation, organizationId, selection } = target;

    if (installation.space_selection === selection) {
        return;
    }

    await apiClient.integrations.updateIntegrationInstallation(integrationName, installation.id, {
        space_selection: selection,
    });

    analytics.logEvent({
        type: 'integration_installation_toggle_space_selection',
        integration: integrationName,
        organization: organizationId,
        spaceSelection: selection,
    });
}

/**
 * Install the integration in the selected sites
 */
export async function installIntegrationInSites(target: {
    integrationName: string;
    installation: api.IntegrationInstallation;
    siteIds: string[];
    organizationId: string;
}): Promise<IntegrationSiteInstallationExtended[]> {
    const { integrationName, installation, siteIds, organizationId } = target;

    await updateIntegrationInstallationSiteSelection({
        integrationName,
        installation,
        organizationId,
        selection: api.IntegrationInstallationSiteSelection.Selected,
    });

    return await pMap(
        siteIds,
        async (siteId) => {
            const { data, error } = await apiClient.integrations.installIntegrationOnSite(
                integrationName,
                installation.id,
                {
                    siteId,
                },
                { extended: true }
            );

            if (!error) {
                analytics.logEvent({
                    type: 'integration_site_install',
                    integration: integrationName,
                    site: siteId,
                    organization: organizationId,
                    withAccountInstallation: false,
                });
            }

            assertIsExtendedSiteInstallation(data);
            return data;
        },
        { concurrency: 5 }
    );
}

/**
 * Install the integration in the selected spaces
 */
export async function installIntegrationInSpaces(target: {
    integrationName: string;
    installation: api.IntegrationInstallation;
    spaceIds: string[];
    organizationId: string;
}): Promise<IntegrationSpaceInstallationExtended[]> {
    const { integrationName, installation, spaceIds, organizationId } = target;

    await updateIntegrationInstallationSpaceSelection({
        integrationName,
        installation,
        organizationId,
        selection: api.IntegrationInstallationSpaceSelection.Selected,
    });

    return await pMap(
        spaceIds,
        async (spaceId) => {
            const { data, error } = await apiClient.integrations.installIntegrationOnSpace(
                integrationName,
                installation.id,
                {
                    space: spaceId,
                },
                { extended: true }
            );

            if (!error) {
                analytics.logEvent({
                    type: 'integration_space_install',
                    integration: integrationName,
                    space: spaceId,
                    organization: organizationId,
                    withAccountInstallation: false,
                });
            }

            assertIsExtendedSpaceInstallation(data);
            return data;
        },

        { concurrency: 5 }
    );
}
